import type { I18n } from '@learnforge/i18n';
import type { RegionSpec } from '../prob/normal';
import { createSvgEl } from '@learnforge/demo-render';
import { niceTickStep, formatTick } from '@learnforge/demo-math/numeric';

export interface PdfCurveOptions {
  fn: (x: number) => number;
  xDomain: [number, number];
  samples?: number;
  shadedRegion?: RegionSpec;
  showSigmaBands?: boolean;
  mu?: number;
  sigma?: number;
  className?: string;
  xLabelKey?: string;
  yLabelKey?: string;
  /**
   * Replace the numeric x-axis ticks with z-score ticks at μ, μ±σ, μ±2σ, μ±3σ.
   * Requires mu and sigma. Useful for normal-distribution charts.
   */
  showZScoreTicks?: boolean;
  i18n?: I18n<string>;
}

export interface PdfCurveHandle {
  group: SVGGElement;
  setFn(fn: (x: number) => number): void;
  setShadedRegion(spec: RegionSpec | null): void;
  destroy(): void;
}

const VIEW_W = 800;
const VIEW_H = 400;
const PAD_L = 56;
const PAD_R = 24;
const PAD_T = 28;
const PAD_B = 56;

export function createPdfCurve(parent: SVGGElement, opts: PdfCurveOptions): PdfCurveHandle {
  const group = createSvgEl('g', { class: 'pb-pdf' });
  parent.appendChild(group);
  let current: PdfCurveOptions = opts;

  function maxY(): number {
    const samples = current.samples ?? 240;
    const [xMin, xMax] = current.xDomain;
    let max = 0;
    for (let i = 0; i <= samples; i++) {
      const x = xMin + ((xMax - xMin) * i) / samples;
      max = Math.max(max, current.fn(x));
    }
    return max * 1.1 || 1;
  }

  function render(): void {
    group.replaceChildren();
    const [xMin, xMax] = current.xDomain;
    const yMax = maxY();
    const samples = current.samples ?? 240;
    const toX = (x: number) => PAD_L + ((x - xMin) / (xMax - xMin)) * (VIEW_W - PAD_L - PAD_R);
    const toY = (y: number) => VIEW_H - PAD_B - (y / yMax) * (VIEW_H - PAD_T - PAD_B);
    const xRange = xMax - xMin;

    // gridlines + y-axis ticks
    const yStep = niceTickStep(yMax, 4);
    for (let y = 0; y <= yMax + 1e-9; y += yStep) {
      const yPx = toY(y);
      group.appendChild(
        createSvgEl('line', {
          class: 'pb-axis-grid',
          x1: PAD_L,
          y1: yPx,
          x2: VIEW_W - PAD_R,
          y2: yPx,
        }),
      );
      const lbl = createSvgEl('text', {
        class: 'pb-axis-tick-label',
        x: PAD_L - 8,
        y: yPx,
        'text-anchor': 'end',
        'dominant-baseline': 'central',
      });
      lbl.textContent = formatTick(y, yStep);
      group.appendChild(lbl);
    }

    // x-axis ticks — either standard numeric or z-score labels relative to (mu, sigma)
    const useZ = current.showZScoreTicks && current.mu !== undefined && current.sigma !== undefined;
    if (useZ) {
      const mu = current.mu!;
      const sigma = current.sigma!;
      for (let k = -3; k <= 3; k++) {
        const x = mu + k * sigma;
        if (x < xMin || x > xMax) continue;
        const xPx = toX(x);
        group.appendChild(
          createSvgEl('line', {
            class: 'pb-axis-tick',
            x1: xPx,
            y1: VIEW_H - PAD_B,
            x2: xPx,
            y2: VIEW_H - PAD_B + 4,
          }),
        );
        const lbl = createSvgEl('text', {
          class: 'pb-axis-tick-label',
          x: xPx,
          y: VIEW_H - PAD_B + 8,
          'text-anchor': 'middle',
          'dominant-baseline': 'hanging',
        });
        lbl.textContent =
          k === 0 ? 'μ' : `${k > 0 ? '+' : '−'}${Math.abs(k) === 1 ? '' : Math.abs(k)}σ`;
        group.appendChild(lbl);
      }
    } else {
      const xStep = niceTickStep(xRange, 8);
      const xStart = Math.ceil(xMin / xStep) * xStep;
      for (let x = xStart; x <= xMax + 1e-9; x += xStep) {
        const xPx = toX(x);
        group.appendChild(
          createSvgEl('line', {
            class: 'pb-axis-tick',
            x1: xPx,
            y1: VIEW_H - PAD_B,
            x2: xPx,
            y2: VIEW_H - PAD_B + 4,
          }),
        );
        const lbl = createSvgEl('text', {
          class: 'pb-axis-tick-label',
          x: xPx,
          y: VIEW_H - PAD_B + 8,
          'text-anchor': 'middle',
          'dominant-baseline': 'hanging',
        });
        lbl.textContent = formatTick(x, xStep);
        group.appendChild(lbl);
      }
    }

    // axis lines
    group.appendChild(
      createSvgEl('line', {
        class: 'pb-axis-line',
        x1: PAD_L,
        y1: VIEW_H - PAD_B,
        x2: VIEW_W - PAD_R,
        y2: VIEW_H - PAD_B,
      }),
    );
    group.appendChild(
      createSvgEl('line', {
        class: 'pb-axis-line',
        x1: PAD_L,
        y1: PAD_T,
        x2: PAD_L,
        y2: VIEW_H - PAD_B,
      }),
    );

    if (current.i18n && current.xLabelKey) {
      const lbl = createSvgEl('text', {
        class: 'pb-axis-label',
        x: PAD_L + (VIEW_W - PAD_L - PAD_R) / 2,
        y: VIEW_H - 12,
        'text-anchor': 'middle',
      });
      lbl.textContent = current.i18n.t(current.xLabelKey);
      group.appendChild(lbl);
    }
    if (current.i18n && current.yLabelKey) {
      const lbl = createSvgEl('text', {
        class: 'pb-axis-label',
        x: 0,
        y: 0,
        'text-anchor': 'middle',
        transform: `translate(16, ${PAD_T + (VIEW_H - PAD_T - PAD_B) / 2}) rotate(-90)`,
      });
      lbl.textContent = current.i18n.t(current.yLabelKey);
      group.appendChild(lbl);
    }

    if (current.shadedRegion) {
      const segments: [number, number][] = [];
      const r = current.shadedRegion;
      switch (r.kind) {
        case 'between':
          segments.push([Math.max(xMin, r.a), Math.min(xMax, r.b)]);
          break;
        case 'leq':
          segments.push([xMin, Math.min(xMax, r.a)]);
          break;
        case 'geq':
          segments.push([Math.max(xMin, r.a), xMax]);
          break;
        case 'tails':
          segments.push([xMin, -Math.abs(r.a)]);
          segments.push([Math.abs(r.a), xMax]);
          break;
      }
      for (const [a, b] of segments) {
        if (a >= b) continue;
        const points: string[] = [`${toX(a).toFixed(2)},${(VIEW_H - PAD_B).toFixed(2)}`];
        const segSamples = Math.max(8, Math.round((samples * (b - a)) / (xMax - xMin)));
        for (let i = 0; i <= segSamples; i++) {
          const x = a + ((b - a) * i) / segSamples;
          points.push(`${toX(x).toFixed(2)},${toY(current.fn(x)).toFixed(2)}`);
        }
        points.push(`${toX(b).toFixed(2)},${(VIEW_H - PAD_B).toFixed(2)}`);
        const poly = createSvgEl('polygon', {
          class: 'pb-pdf-shade',
          points: points.join(' '),
        });
        group.appendChild(poly);
      }
    }

    const points: string[] = [];
    for (let i = 0; i <= samples; i++) {
      const x = xMin + ((xMax - xMin) * i) / samples;
      points.push(`${i === 0 ? 'M' : 'L'} ${toX(x).toFixed(2)} ${toY(current.fn(x)).toFixed(2)}`);
    }
    const path = createSvgEl('path', {
      class: `pb-pdf-curve ${current.className ?? ''}`.trim(),
      d: points.join(' '),
      fill: 'none',
    });
    group.appendChild(path);

    if (current.showSigmaBands && current.mu !== undefined && current.sigma !== undefined) {
      for (const k of [1, 2, 3]) {
        const left = current.mu - k * current.sigma;
        const right = current.mu + k * current.sigma;
        if (left < xMin || right > xMax) continue;
        const band = createSvgEl('rect', {
          class: `pb-pdf-sigma-band pb-pdf-sigma-band-${k}`,
          x: toX(left),
          y: PAD_T,
          width: toX(right) - toX(left),
          height: VIEW_H - PAD_T - PAD_B,
        });
        group.insertBefore(band, group.firstChild);
      }
    }
  }

  render();

  return {
    group,
    setFn(fn) {
      current = { ...current, fn };
      render();
    },
    setShadedRegion(spec) {
      if (spec === null) {
        const { shadedRegion: _, ...rest } = current;
        current = rest as PdfCurveOptions;
      } else {
        current = { ...current, shadedRegion: spec };
      }
      render();
    },
    destroy() {
      group.remove();
    },
  };
}
